/**
 * Centralised client-session teardown (§13.6).
 *
 * Wipes ALL client-held user state so no data leaks between accounts on a
 * shared browser. Call it on:
 *   - explicit logout (useLogoutMutation),
 *   - forced logout when the refresh cookie is gone (the `auth:logout` event),
 *   - and, defensively, at the START of a successful login so a new user always
 *     begins from a clean slate even when the previous session ended abnormally
 *     (tab closed, crash, or an interrupted logout).
 *
 * What it clears:
 *   - the in-memory access token,
 *   - the auth store (current user identity),
 *   - the manual-voucher store (per-user JV/PV posting results),
 *   - the file-preview modal state (may reference the prior user's file path),
 *   - the entire TanStack Query cache (all fetched server data; cancels in-flight),
 *   - sessionStorage (transient per-tab data).
 *
 * What it deliberately KEEPS:
 *   - The HttpOnly `refresh_token` cookie is not JS-accessible; the backend
 *     `/auth/logout` endpoint both revokes the token server-side AND deletes the
 *     cookie, so there is nothing for the client to clear there.
 *   - The ui-store theme/density/sidebar prefs in localStorage — these are
 *     per-browser cosmetic preferences, not user data, and wiping them on every
 *     logout is user-hostile. Pass `{ keepUiPrefs: false }` to purge them too.
 */

import { clearAccessToken } from '@/lib/auth/tokens';
import { queryClient } from '@/lib/query-client';
import { useAuthStore } from '@/stores/auth-store';
import { useFilePreviewStore } from '@/stores/file-preview-store';
import { useManualVoucherStore } from '@/stores/manual-voucher-store';
import { UI_PERSIST_KEY } from '@/stores/ui-store';

export interface ResetSessionOptions {
  /** Keep cosmetic UI prefs (theme / density / sidebar). Default: true. */
  keepUiPrefs?: boolean;
}

/**
 * Tear down every trace of the current user's session on this client.
 * Safe to call when already logged out (all operations are idempotent).
 */
export function resetClientSession(options: ResetSessionOptions = {}): void {
  const { keepUiPrefs = true } = options;

  // 1. Credentials + identity.
  clearAccessToken();
  useAuthStore.getState().logout();

  // 2. Per-user in-memory stores.
  useManualVoucherStore.getState().clearAll();
  useFilePreviewStore.getState().close();

  // 3. All fetched server data — removes cached queries/mutations and cancels
  //    any in-flight requests so the next user never sees stale rows.
  queryClient.clear();

  // 4. Browser storage. sessionStorage is always cleared; the persisted UI
  //    prefs survive unless the caller opts out.
  try {
    window.sessionStorage.clear();
    if (!keepUiPrefs) {
      window.localStorage.removeItem(UI_PERSIST_KEY);
    }
  } catch {
    // private-mode / quota errors — the in-memory clears above are what matter.
  }
}
